import {
  faGear,
  faPaperPlane,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { BulkSmsHistoryItem } from 'api/model/bulkSmsHistoryItem';
import type { BulkSmsRecipient } from 'api/model/bulkSmsRecipient';
import type { SendBulkSmsResult } from 'api/model/sendBulkSmsResult';
import {
  useQualityGetBulkSmsHistory,
  useQualityGetBulkSmsRecipients,
  useQualitySendBulkSms,
} from 'api/quality/quality';
import Dropdown from 'components/Dropdown';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import RadioButton from 'components/inputs/RadioButton';
import ConfirmModal from 'components/Modal/ConfirmModal';
import themes from 'constants/Colors';
import themeSizes from 'constants/Sizes';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import styled from 'styled-components';
import { useLocalStorage } from 'usehooks-ts';
import { showException } from 'utils/exception-helper';
import { measureSms } from './smsSegments';

/**
 * Kvalitet (BETA) › SMS-utskick.
 *
 * Skriv ett meddelande och skicka det till fälttestarna — hela kåren eller en/flera grupper
 * (tabellen Teams, kopplad via AppUsers.TeamId). Mottagarlistan visas innan utskicket och går
 * att kryssa ur person för person, eftersom ett mass-SMS varken går att ångra eller korrigera
 * i efterhand.
 *
 * Mottagarunderlaget hämtas ofiltrerat (alla aktiva fälttestare) och filtreras i klienten, så
 * att gruppvalet känner till samtliga grupper oavsett vad som är valt just nu. Backend gör om
 * urvalet vid utskicket — vyns lista är ett urval, inte ett löfte.
 *
 * Kugghjulet i gruppväljaren gömmer undan grupper man aldrig skickar till. Det är ett rent
 * visuellt bortval per webbläsare (localStorage `BulkSmsHiddenTeams`) — en gömd grupp tas
 * samtidigt ur urvalet, så ingen kan bli vald utan att synas.
 */

const PageContainer = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PageTitle = styled.h1`
  font-size: ${themeSizes.font.large};
  margin-bottom: 10px;
`;

const Card = styled.div`
  padding: 15px;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const CardTitle = styled.h2`
  font-size: ${themeSizes.font.medium};
  margin-bottom: 5px;
  padding-bottom: 5px;
  border-bottom: 1px solid ${themes.light.border.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
`;

const MessageInput = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font: inherit;
  font-size: 14px;
  resize: vertical;
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #666;
`;

const OptionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 15px;
`;

/**
 * Kryssrutor och radioknappar kommer från components/inputs, inte från webbläsarens
 * default — de renderas i Zäkras orange (theme.colors.background.button) som i
 * Globala inställningar. Wrappern är redan en <label>, så texten skickas som children.
 */
const OptionLabel = styled(Checkbox)`
  align-items: center;
  font-size: 13px;

  input:disabled + div {
    cursor: not-allowed;
  }
`;

const AudienceOption = styled(RadioButton)`
  align-items: center;
  font-size: 13px;
`;

const TeamSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  background-color: #f8f9fa;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
`;

const TeamSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  font-size: 12px;
  color: #666;
`;

const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 6px;
`;

const GearButton = styled.button`
  border: none;
  background: none;
  padding: 2px 4px;
  color: #6c757d;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.foreground.tint};
  }
`;

const TeamVisibilityMenu = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 10px;
  min-width: 220px;
  max-height: 320px;
  overflow: auto;
  font-size: 13px;
`;

const TeamVisibilityTitle = styled.div`
  font-weight: 500;
  padding-bottom: 5px;
  margin-bottom: 2px;
  border-bottom: 1px solid ${themes.light.border.primary};
`;

const SearchInput = styled.input`
  padding: 5px 8px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 13px;
  min-width: 200px;
`;

const RecipientTableWrapper = styled.div`
  max-height: 400px;
  overflow: auto;
  border: 1px solid ${themes.light.border.primary};
  border-radius: 4px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Warning = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 4px;
  background-color: #fd7e1415;
  border: 1px solid #fd7e14;
  font-size: 13px;
`;

const LinkButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  color: ${({ theme }) => theme.colors.foreground.tint};
  cursor: pointer;
  font-size: inherit;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }
`;

const MessageQuote = styled.div`
  padding: 10px;
  background-color: #f8f9fa;
  border-left: 3px solid ${({ theme }) => theme.colors.foreground.tint};
  border-radius: 4px;
  font-size: 13px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const COLORS = {
  red: '#dc3545',
  green: '#28a745',
  gray: '#6c757d',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px',
  borderBottom: '2px solid #ddd',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  fontSize: '12px',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '6px 8px',
  fontSize: '13px',
  verticalAlign: 'top',
};

/** Samma tak som BulkSmsLogic.MaxMessageLength. */
const MAX_MESSAGE_LENGTH = 1000;

const NO_TEAM = -1;

const formatDateTime = (date: Date): string =>
  new Date(date).toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

const teamKey = (recipient: BulkSmsRecipient): number =>
  recipient.teamId ?? NO_TEAM;

const teamLabel = (recipient: BulkSmsRecipient): string =>
  recipient.teamName ?? 'Utan grupp';

const HistoryRow = ({ item }: { item: BulkSmsHistoryItem }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td style={tdStyle}>{formatDateTime(item.sentAt)}</td>
        <td style={tdStyle}>{item.sentByName}</td>
        <td style={tdStyle}>{item.audienceDescription}</td>
        <td style={{ ...tdStyle, maxWidth: '400px' }}>{item.message}</td>
        <td style={tdStyle}>{item.recipientCount}</td>
        <td
          style={{
            ...tdStyle,
            color: item.failedCount > 0 ? COLORS.red : COLORS.gray,
          }}
        >
          {item.failedCount}
        </td>
        <td style={tdStyle}>
          <LinkButton onClick={() => setExpanded((e) => !e)} type="button">
            {expanded ? 'Dölj' : 'Visa mottagare'}
          </LinkButton>
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={7} style={{ ...tdStyle, backgroundColor: '#f8f9fa' }}>
            <Table>
              <tbody>
                {item.recipients.map((r) => (
                  <tr key={`${item.id}-${r.userId}`}>
                    <td style={tdStyle}>{r.name}</td>
                    <td style={tdStyle}>{r.teamName ?? '-'}</td>
                    <td style={tdStyle}>{r.phoneNumber}</td>
                    <td
                      style={{
                        ...tdStyle,
                        color: r.succeeded ? COLORS.green : COLORS.red,
                      }}
                    >
                      {r.succeeded
                        ? 'Skickat'
                        : `Misslyckades: ${r.errorMessage}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </td>
        </tr>
      )}
    </>
  );
};

const BulkSmsPage = () => {
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState<'all' | 'teams'>('all');
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>([]);
  // Enskilt urkryssade personer. Hålls separat från gruppvalet så att en urkryssning inte
  // tappas när man växlar grupp fram och tillbaka.
  const [deselectedUserIds, setDeselectedUserIds] = useState<Set<number>>(
    new Set()
  );
  const [search, setSearch] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [lastResult, setLastResult] = useState<SendBulkSmsResult | null>(null);
  // Grupper som gömts undan via kugghjulet. Rent visuellt bortval — sparas per webbläsare
  // så att den som aldrig skickar till halva kåren slipper scrolla förbi dem varje gång.
  const [hiddenTeamIds, setHiddenTeamIds] = useLocalStorage<number[]>(
    'BulkSmsHiddenTeams',
    []
  );
  const [teamMenuOpen, setTeamMenuOpen] = useState(false);

  const { data: recipientData, isLoading } = useQualityGetBulkSmsRecipients();
  const { data: history = [], refetch: refetchHistory } =
    useQualityGetBulkSmsHistory();
  const { mutateAsync: sendBulkSms, isLoading: isSending } =
    useQualitySendBulkSms();

  const allRecipients = useMemo(
    () => recipientData?.recipients ?? [],
    [recipientData]
  );

  const teams = useMemo(() => {
    const byId = new Map<number, { id: number; name: string; count: number }>();
    allRecipients.forEach((r) => {
      const id = teamKey(r);
      const existing = byId.get(id);
      if (existing) {
        existing.count += 1;
      } else {
        byId.set(id, { id, name: teamLabel(r), count: 1 });
      }
    });
    return [...byId.values()].sort((a, b) => {
      // "Utan grupp" sist, annars alfabetiskt.
      if (a.id === NO_TEAM) return 1;
      if (b.id === NO_TEAM) return -1;
      return a.name.localeCompare(b.name, 'sv');
    });
  }, [allRecipients]);

  /** Grupperna som faktiskt visas i gruppväljaren. */
  const visibleTeams = useMemo(
    () => teams.filter((t) => !hiddenTeamIds.includes(t.id)),
    [teams, hiddenTeamIds]
  );

  /** Mottagarna som gruppvalet omfattar, innan enskilda urkryssningar. */
  const audienceRecipients = useMemo(() => {
    if (audience === 'all') return allRecipients;
    return allRecipients.filter((r) => selectedTeamIds.includes(teamKey(r)));
  }, [allRecipients, audience, selectedTeamIds]);

  const selectedRecipients = useMemo(
    () =>
      audienceRecipients.filter(
        (r) => r.canReceiveSms && !deselectedUserIds.has(r.userId)
      ),
    [audienceRecipients, deselectedUserIds]
  );

  const visibleRecipients = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return audienceRecipients;
    return audienceRecipients.filter(
      (r) =>
        r.name.toLowerCase().includes(needle) ||
        (r.teamName ?? '').toLowerCase().includes(needle) ||
        r.phoneNumber.toLowerCase().includes(needle)
    );
  }, [audienceRecipients, search]);

  const withoutPhone = audienceRecipients.filter((r) => !r.canReceiveSms);

  const sms = measureSms(message);
  const tooLong = message.length > MAX_MESSAGE_LENGTH;
  const canSend =
    message.trim().length > 0 && !tooLong && selectedRecipients.length > 0;

  const audienceDescription =
    audience === 'all'
      ? 'Alla fälttestare'
      : teams
          .filter((t) => selectedTeamIds.includes(t.id))
          .map((t) => t.name)
          .join(', ') || 'Ingen grupp vald';

  const toggleTeam = (id: number) => {
    setSelectedTeamIds((current) =>
      current.includes(id) ? current.filter((t) => t !== id) : [...current, id]
    );
  };

  /**
   * Visa/göm en grupp i väljaren. En gömd grupp tas samtidigt ur urvalet — annars skulle den
   * ligga kvar som vald utan att synas någonstans, och tyst följa med i utskicket.
   */
  const toggleTeamVisibility = (id: number) => {
    const hide = !hiddenTeamIds.includes(id);
    setHiddenTeamIds(
      hide ? [...hiddenTeamIds, id] : hiddenTeamIds.filter((t) => t !== id)
    );
    if (hide) {
      setSelectedTeamIds((current) => current.filter((t) => t !== id));
    }
  };

  const toggleRecipient = (userId: number) => {
    setDeselectedUserIds((current) => {
      const next = new Set(current);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  /** Markera/avmarkera alla som syns just nu (dvs. även sökfiltret räknas). */
  const setAllVisible = (selected: boolean) => {
    setDeselectedUserIds((current) => {
      const next = new Set(current);
      visibleRecipients.forEach((r) => {
        if (selected) {
          next.delete(r.userId);
        } else {
          next.add(r.userId);
        }
      });
      return next;
    });
  };

  const send = async () => {
    setShowConfirm(false);
    try {
      const result = await sendBulkSms({
        data: {
          message: message.trim(),
          // Tom lista = hela kåren. NO_TEAM följer med som den är; backend skriver ut den
          // som "Utan grupp" i historiken (BulkSmsLogic.NoTeamId).
          teamIds: audience === 'teams' ? selectedTeamIds : [],
          userIds: selectedRecipients.map((r) => r.userId),
        },
      });

      setLastResult(result);
      if (result.failedCount === 0) {
        toast.success(`SMS skickat till ${result.successCount} fälttestare`);
      } else {
        toast.error(
          `${result.successCount} av ${result.recipientCount} SMS skickades — se resultatet nedan`
        );
      }
      // Rensa meddelandet så att ett andra klick inte skickar samma sak en gång till.
      setMessage('');
      await refetchHistory();
    } catch (error) {
      showException(error);
    }
  };

  return (
    <PageContainer>
      <div>
        <PageTitle>SMS-utskick</PageTitle>
        <div style={{ fontSize: '13px', color: '#666' }}>
          Skicka ett meddelande till fälttestarna. Utskicket går iväg direkt och
          kan inte ångras.
        </div>
      </div>

      <Card>
        <CardTitle>Meddelande</CardTitle>
        <MessageInput
          maxLength={MAX_MESSAGE_LENGTH}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Skriv meddelandet som ska skickas till fälttestarna..."
          value={message}
        />
        <MetaRow>
          <span>
            {message.length} / {MAX_MESSAGE_LENGTH} tecken
            {sms.segments > 0 &&
              ` · ${sms.segments} SMS per mottagare (${sms.encoding}, ${sms.remainingInSegment} tecken kvar)`}
          </span>
          {sms.encoding === 'UCS-2' && sms.segments > 0 && (
            <span style={{ color: '#fd7e14' }}>
              Meddelandet innehåller tecken utanför SMS-standarden (t.ex. emoji)
              — bara 70 tecken ryms per SMS.
            </span>
          )}
        </MetaRow>
      </Card>

      <Card>
        <CardTitle>
          Mottagare
          <span style={{ fontSize: '13px', fontWeight: 'normal' }}>
            {selectedRecipients.length} valda
            {audienceRecipients.length > 0 &&
              ` av ${audienceRecipients.length} i urvalet`}
          </span>
        </CardTitle>

        <OptionRow>
          <AudienceOption
            checked={audience === 'all'}
            onChange={() => setAudience('all')}
          >
            Alla fälttestare ({allRecipients.length})
          </AudienceOption>
          <AudienceOption
            checked={audience === 'teams'}
            onChange={() => setAudience('teams')}
          >
            Valda grupper
          </AudienceOption>
        </OptionRow>

        {audience === 'teams' && (
          <TeamSection>
            <TeamSectionHeader>
              <span>
                {selectedTeamIds.length} av {visibleTeams.length} grupper valda
                {hiddenTeamIds.length > 0 && ` · ${hiddenTeamIds.length} dolda`}
              </span>
              <Dropdown
                content={
                  teamMenuOpen && (
                    <TeamVisibilityMenu>
                      <TeamVisibilityTitle>
                        Visa grupper i listan
                      </TeamVisibilityTitle>
                      {teams.map((team) => (
                        <OptionLabel
                          checked={!hiddenTeamIds.includes(team.id)}
                          key={team.id}
                          onChange={() => toggleTeamVisibility(team.id)}
                        >
                          {team.name} ({team.count})
                        </OptionLabel>
                      ))}
                      {hiddenTeamIds.length > 0 && (
                        <LinkButton
                          onClick={() => setHiddenTeamIds([])}
                          type="button"
                        >
                          Visa alla grupper
                        </LinkButton>
                      )}
                    </TeamVisibilityMenu>
                  )
                }
                onLostFocus={() => setTeamMenuOpen(false)}
                position="left"
              >
                <GearButton
                  aria-label="Välj vilka grupper som visas"
                  onClick={() => setTeamMenuOpen((open) => !open)}
                  title="Välj vilka grupper som visas"
                  type="button"
                >
                  <FontAwesomeIcon icon={faGear} />
                </GearButton>
              </Dropdown>
            </TeamSectionHeader>
            {visibleTeams.length === 0 ? (
              <span style={{ fontSize: '13px', color: '#666' }}>
                Alla grupper är dolda — visa någon via kugghjulet.
              </span>
            ) : (
              <TeamGrid>
                {visibleTeams.map((team) => (
                  <OptionLabel
                    checked={selectedTeamIds.includes(team.id)}
                    key={team.id}
                    onChange={() => toggleTeam(team.id)}
                  >
                    {team.name} ({team.count})
                  </OptionLabel>
                ))}
              </TeamGrid>
            )}
          </TeamSection>
        )}

        {withoutPhone.length > 0 && (
          <Warning>
            <FontAwesomeIcon icon={faTriangleExclamation} />
            {withoutPhone.length} i urvalet saknar giltigt mobilnummer och kan
            inte få SMS: {withoutPhone.map((r) => r.name).join(', ')}
          </Warning>
        )}

        <OptionRow>
          <SearchInput
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Sök namn, grupp eller nummer"
            value={search}
          />
          <LinkButton onClick={() => setAllVisible(true)} type="button">
            Markera alla{search ? ' träffar' : ''}
          </LinkButton>
          <LinkButton onClick={() => setAllVisible(false)} type="button">
            Avmarkera alla{search ? ' träffar' : ''}
          </LinkButton>
        </OptionRow>

        <RecipientTableWrapper>
          <Table>
            <thead>
              <tr>
                <th aria-label="Vald" style={{ ...thStyle, width: '30px' }} />
                <th style={thStyle}>Fälttestare</th>
                <th style={thStyle}>Grupp</th>
                <th style={thStyle}>Gruppledare</th>
                <th style={thStyle}>Mobilnummer</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr>
                  <td colSpan={5} style={tdStyle}>
                    Hämtar fälttestare...
                  </td>
                </tr>
              )}
              {!isLoading && visibleRecipients.length === 0 && (
                <tr>
                  <td colSpan={5} style={tdStyle}>
                    Inga fälttestare i urvalet.
                  </td>
                </tr>
              )}
              {visibleRecipients.map((r) => (
                <tr
                  key={r.userId}
                  style={{ opacity: r.canReceiveSms ? 1 : 0.5 }}
                >
                  <td style={tdStyle}>
                    <OptionLabel
                      aria-label={`Skicka till ${r.name}`}
                      checked={
                        r.canReceiveSms && !deselectedUserIds.has(r.userId)
                      }
                      disabled={!r.canReceiveSms}
                      onChange={() => toggleRecipient(r.userId)}
                    />
                  </td>
                  <td style={tdStyle}>{r.name}</td>
                  <td style={tdStyle}>{r.teamName ?? '-'}</td>
                  <td style={tdStyle}>{r.groupLeaderName ?? '-'}</td>
                  <td style={tdStyle}>
                    {r.canReceiveSms ? (
                      r.phoneNumber
                    ) : (
                      <span style={{ color: COLORS.red }}>
                        {r.phoneNumber || 'Saknas'}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </RecipientTableWrapper>
      </Card>

      <div>
        <Button
          disabled={!canSend || isSending}
          icon={<FontAwesomeIcon icon={faPaperPlane} />}
          loading={isSending}
          onClick={() => setShowConfirm(true)}
        >
          {`Skicka till ${selectedRecipients.length} fälttestare`}
        </Button>
      </div>

      {lastResult && (
        <Card>
          <CardTitle>Senaste utskicket</CardTitle>
          <div style={{ fontSize: '13px' }}>
            {lastResult.successCount} av {lastResult.recipientCount} SMS
            skickades.
          </div>
          {lastResult.skippedRecipientNames.length > 0 && (
            <Warning>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              Hoppade över {lastResult.skippedRecipientNames.length} valda
              mottagare: {lastResult.skippedRecipientNames.join(', ')}
            </Warning>
          )}
          {lastResult.failures.length > 0 && (
            <Table>
              <thead>
                <tr>
                  <th style={thStyle}>Fälttestare</th>
                  <th style={thStyle}>Nummer</th>
                  <th style={thStyle}>Fel</th>
                </tr>
              </thead>
              <tbody>
                {lastResult.failures.map((f) => (
                  <tr key={f.userId}>
                    <td style={tdStyle}>{f.name}</td>
                    <td style={tdStyle}>{f.phoneNumber}</td>
                    <td style={{ ...tdStyle, color: COLORS.red }}>
                      {f.errorMessage}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      <Card>
        <CardTitle>Tidigare utskick</CardTitle>
        {history.length === 0 ? (
          <div style={{ fontSize: '13px', color: '#666' }}>
            Inga utskick gjorda ännu.
          </div>
        ) : (
          <RecipientTableWrapper>
            <Table>
              <thead>
                <tr>
                  <th style={thStyle}>Tidpunkt</th>
                  <th style={thStyle}>Skickat av</th>
                  <th style={thStyle}>Urval</th>
                  <th style={thStyle}>Meddelande</th>
                  <th style={thStyle}>Mottagare</th>
                  <th style={thStyle}>Misslyckade</th>
                  <th aria-label="Mottagare" style={thStyle} />
                </tr>
              </thead>
              <tbody>
                {history.map((item) => (
                  <HistoryRow item={item} key={item.id} />
                ))}
              </tbody>
            </Table>
          </RecipientTableWrapper>
        )}
      </Card>

      {showConfirm && (
        <ConfirmModal
          confirmText={`Skicka till ${selectedRecipients.length}`}
          onCancel={() => setShowConfirm(false)}
          onConfirm={send}
          title="Skicka SMS-utskick"
        >
          <div style={{ fontSize: '13px', marginBottom: '10px' }}>
            Meddelandet skickas som {sms.segments} SMS till{' '}
            <strong>{selectedRecipients.length} fälttestare</strong> (
            {audienceDescription}). Utskicket kan inte ångras.
          </div>
          <MessageQuote>{message}</MessageQuote>
        </ConfirmModal>
      )}
    </PageContainer>
  );
};

export default BulkSmsPage;
